import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Printer, Loader2, Bot } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { vendorInvoiceApi } from '../../../services/vendorInvoiceService';
import { useVendorProfile } from '../../../services/vendorProfileService';
import { useToastContext } from '../../../contexts/ToastContext';
import useVendorPlanAccess from '../../../hooks/useVendorPlanAccess';
import { supportChatApi } from '../../../services/supportChatService';
import { normalizeChatReply } from '../../../utils/chatWidgetUtils';
import Button from '../../shared/Button';
import Input from '../../shared/Input';
import CustomSelect from '../../shared/CustomSelect';
import DatePicker from '../../client/DatePicker';
import BrandImagePicker from '../branding/BrandImagePicker';
import InvoiceLivePreview from './InvoiceLivePreview';

const emptyLine = () => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  description: '',
  quantity: '1',
  unit_price: '',
});

const resolveDiscountAmount = (subtotal, discountType, discountValue) => {
  const sub = Number(subtotal) || 0;
  const value = parseFloat(discountValue);
  if (!discountType || Number.isNaN(value) || value <= 0) return 0;
  if (discountType === 'percent') {
    return Math.min(sub, Math.round(sub * (Math.min(100, value) / 100) * 100) / 100);
  }
  return Math.min(sub, Math.round(value * 100) / 100);
};

const lineItemsFromBooking = (booking) => {
  if (!booking) return [emptyLine()];

  const description =
    booking.line_item_description ||
    [booking.service_name, booking.package_name].filter(Boolean).join(' — ') ||
    'Booking service';

  const price =
    booking.line_item_unit_price ??
    booking.subtotal_amount ??
    null;

  return [
    {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      description,
      quantity: '1',
      unit_price: price != null && price !== '' ? String(price) : '',
    },
  ];
};

const addDaysIso = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const CustomInvoiceBuilder = () => {
  const navigate = useNavigate();
  const { id: editInvoiceId } = useParams();
  const isEdit = Boolean(editInvoiceId);
  const queryClient = useQueryClient();
  const { success, error: showError } = useToastContext();
  const { data: profileResp } = useVendorProfile();
  const { isFreeTier, hasActiveSubscription } = useVendorPlanAccess();
  const canUseAiGenerator = hasActiveSubscription === true && !isFreeTier;

  const vp = profileResp?.data ?? {};
  const vendorLabel = vp.business_name || 'Your business';
  const profileLogo = vp.logo_url || vp.brand_settings?.logo_url || null;
  const vendorAddressLines = [
    vp.address,
    [vp.city, vp.state, vp.zip_code].filter(Boolean).join(', '),
    vp.country,
    vp.business_phone,
  ].filter(Boolean);

  const [recipientType, setRecipientType] = useState('client');
  const [clientId, setClientId] = useState('');
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [bookingId, setBookingId] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState(() => addDaysIso(14));
  const [taxRate, setTaxRate] = useState('');
  const [discountType, setDiscountType] = useState('');
  const [discountValue, setDiscountValue] = useState('');
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoInitialized, setLogoInitialized] = useState(false);
  const [notes, setNotes] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [status, setStatus] = useState('draft');
  const [lineItems, setLineItems] = useState([emptyLine()]);
  const [previewInvoiceNumber, setPreviewInvoiceNumber] = useState('');
  const [isGeneratingNotes, setIsGeneratingNotes] = useState(false);
  const [notesAiError, setNotesAiError] = useState('');
  const hydratedInvoiceIdRef = useRef(null);

  const { data: invoiceRes, isLoading: loadingInvoice, error: invoiceError } = useQuery({
    queryKey: ['vendor-invoice', editInvoiceId],
    queryFn: () => vendorInvoiceApi.getOne(editInvoiceId),
    enabled: isEdit,
  });

  const { data: clientsRes, isLoading: clientsLoading } = useQuery({
    queryKey: ['invoice-billable-clients'],
    queryFn: () => vendorInvoiceApi.getBillableClients(),
  });

  const clients = useMemo(() => clientsRes?.data ?? [], [clientsRes?.data]);

  const { data: bookingsRes, isLoading: bookingsLoading } = useQuery({
    queryKey: ['invoice-booking-options', recipientType === 'client' ? clientId || 'all' : 'all'],
    queryFn: () =>
      vendorInvoiceApi.getBookingOptions(
        recipientType === 'client' && clientId ? clientId : null
      ),
  });

  const bookings = useMemo(() => bookingsRes?.data ?? [], [bookingsRes?.data]);

  useEffect(() => {
    if (isEdit) return;
    if (recipientType === 'client') {
      setGuestName('');
      setGuestEmail('');
      setGuestPhone('');
    } else {
      setClientId('');
    }
    setBookingId('');
  }, [recipientType, isEdit]);

  useEffect(() => {
    if (isEdit) return;
    if (recipientType === 'client') {
      setBookingId('');
    }
  }, [clientId, recipientType, isEdit]);

  useEffect(() => {
    if (isEdit || logoInitialized) return;
    if (profileLogo) {
      setLogoUrl(profileLogo);
      setLogoInitialized(true);
    } else if (profileResp) {
      setLogoInitialized(true);
    }
  }, [isEdit, profileLogo, profileResp, logoInitialized]);

  useEffect(() => {
    hydratedInvoiceIdRef.current = null;
  }, [editInvoiceId]);

  useEffect(() => {
    if (!isEdit || !invoiceRes?.data) return;
    const inv = invoiceRes.data;
    if (inv.id !== editInvoiceId) return;
    if (hydratedInvoiceIdRef.current === inv.id) return;
    if (['paid', 'cancelled'].includes(String(inv.status || '').toLowerCase())) {
      showError('This invoice cannot be edited.');
      navigate(`/vendor/invoices/${editInvoiceId}`, { replace: true });
      return;
    }
    hydratedInvoiceIdRef.current = inv.id;
    const isGuestInvoice = !inv.client_id && (inv.guest_name || inv.guest_email);
    setRecipientType(isGuestInvoice ? 'guest' : 'client');
    setClientId(inv.client_id || '');
    setGuestName(inv.guest_name || '');
    setGuestEmail(inv.guest_email || '');
    setGuestPhone(inv.guest_phone || '');
    setBookingId(inv.booking_id || '');
    const idate = inv.invoice_date ? String(inv.invoice_date).slice(0, 10) : '';
    const ddate = inv.due_date ? String(inv.due_date).slice(0, 10) : '';
    setInvoiceDate(idate || new Date().toISOString().slice(0, 10));
    setDueDate(ddate || addDaysIso(14));
    setTaxRate(inv.tax_rate != null && inv.tax_rate !== '' ? String(inv.tax_rate) : '');
    setDiscountType(inv.discount_type || '');
    setDiscountValue(
      inv.discount_value != null && inv.discount_value !== '' ? String(inv.discount_value) : ''
    );
    setLogoUrl(inv.logo_url || profileLogo || null);
    setLogoInitialized(true);
    setNotes(inv.notes || '');
    setPaymentTerms(inv.payment_terms || '');
    setStatus(inv.status || 'draft');
    setPreviewInvoiceNumber(inv.invoice_number || '');
    const raw = inv.line_items;
    if (raw?.length) {
      setLineItems(
        raw.map((li, i) => ({
          id: `edit-${i}-${li.description?.slice(0, 8) || i}`,
          description: li.description ?? '',
          quantity: String(li.quantity ?? 1),
          unit_price: li.unit_price != null ? String(li.unit_price) : '',
        }))
      );
    } else {
      setLineItems([emptyLine()]);
    }
  }, [isEdit, invoiceRes, editInvoiceId, navigate, showError, profileLogo]);

  const selectedClient = useMemo(
    () => clients.find((c) => c.id === clientId),
    [clients, clientId]
  );

  const selectedBooking = useMemo(
    () => bookings.find((b) => b.id === bookingId),
    [bookings, bookingId]
  );

  const { subtotal, discountAmount, taxAmount, total, taxRateNum } = useMemo(() => {
    const sub = lineItems.reduce((sum, row) => {
      const q = parseFloat(row.quantity) || 0;
      const u = parseFloat(row.unit_price) || 0;
      return sum + q * u;
    }, 0);
    const discount = resolveDiscountAmount(sub, discountType, discountValue);
    const taxable = Math.max(0, sub - discount);
    const tr = parseFloat(taxRate) || 0;
    const tax = taxable * (tr / 100);
    return {
      subtotal: sub,
      discountAmount: discount,
      taxAmount: tax,
      total: taxable + tax,
      taxRateNum: tr,
    };
  }, [lineItems, taxRate, discountType, discountValue]);

  const clientOptions = useMemo(
    () => clients.map((c) => ({ value: c.id, label: `${c.name || 'Client'} · ${c.email || ''}` })),
    [clients]
  );

  const bookingOptions = useMemo(() => {
    const opts = [{ value: '', label: 'No linked booking (standalone invoice)' }];
    bookings.forEach((b) => {
      const ref = b.booking_reference || b.id?.slice(0, 8);
      const svc = b.service_name ? ` · ${b.service_name}` : '';
      const guest = b.guest_name ? ` · ${b.guest_name}` : '';
      const privateTag = b.is_private_booking || b.booking_source === 'vendor_manual' ? ' · Private' : '';
      opts.push({
        value: b.id,
        label: `${ref}${svc}${guest}${privateTag}${b.event_date ? ` · ${b.event_date}` : ''}`,
      });
    });
    return opts;
  }, [bookings]);

  const applyBookingToForm = (booking) => {
    if (!booking) return;
    if (booking.client_id) {
      setRecipientType('client');
      setClientId(booking.client_id);
      setGuestName('');
      setGuestEmail('');
      setGuestPhone('');
    } else if (booking.guest_name) {
      setRecipientType('guest');
      setClientId('');
      setGuestName(booking.guest_name || '');
      setGuestEmail(booking.guest_email || '');
      setGuestPhone(booking.guest_phone || '');
    }
    setLineItems(lineItemsFromBooking(booking));
  };

  const createMutation = useMutation({
    mutationFn: (payload) => vendorInvoiceApi.createCustom(payload),
    onSuccess: (res) => {
      success(res?.message || 'Invoice created.');
      queryClient.invalidateQueries({ queryKey: ['vendorInvoices'] });
      const newId = res?.data?.id;
      if (newId) navigate(`/vendor/invoices/${newId}`, { replace: true });
      else navigate('/vendor/invoices');
    },
    onError: (e) => {
      const msg = e?.response?.data?.message || 'Failed to create invoice.';
      const errs = e?.response?.data?.errors;
      if (errs && typeof errs === 'object') {
        const first = Object.values(errs).flat()[0];
        showError(first || msg);
      } else {
        showError(msg);
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: (payload) => vendorInvoiceApi.update(editInvoiceId, payload),
    onSuccess: (res) => {
      success(res?.message || 'Invoice updated.');
      queryClient.invalidateQueries({ queryKey: ['vendorInvoices'] });
      queryClient.invalidateQueries({ queryKey: ['vendor-invoice', editInvoiceId] });
      navigate(`/vendor/invoices/${editInvoiceId}`, { replace: true });
    },
    onError: (e) => {
      const msg = e?.response?.data?.message || 'Failed to update invoice.';
      const errs = e?.response?.data?.errors;
      if (errs && typeof errs === 'object') {
        const first = Object.values(errs).flat()[0];
        showError(first || msg);
      } else {
        showError(msg);
      }
    },
  });

  const updateLine = (id, field, value) => {
    setLineItems((rows) =>
      rows.map((r) => (r.id === id ? { ...r, [field]: value } : r))
    );
  };

  const addLine = () => setLineItems((rows) => [...rows, emptyLine()]);
  const removeLine = (id) =>
    setLineItems((rows) => (rows.length <= 1 ? rows : rows.filter((r) => r.id !== id)));

  const handleBookingChange = (value) => {
    setBookingId(value);
    if (!value) return;
    const booking = bookings.find((b) => b.id === value);
    if (booking) {
      applyBookingToForm(booking);
    }
  };

  const billToPreview = useMemo(() => {
    if (recipientType === 'guest') {
      return {
        name: guestName,
        email: guestEmail,
        phone: guestPhone,
      };
    }
    return {
      name: selectedClient?.name,
      email: selectedClient?.email,
      phone: selectedClient?.phone,
    };
  }, [recipientType, guestName, guestEmail, guestPhone, selectedClient]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (recipientType === 'client' && !clientId) {
      showError('Select a platform client or switch to guest / external contact.');
      return;
    }
    if (recipientType === 'guest' && !guestName.trim()) {
      showError('Enter the guest or external contact name.');
      return;
    }
    const payloadLines = lineItems
      .map((row) => ({
        description: row.description.trim(),
        quantity: parseFloat(row.quantity),
        unit_price: parseFloat(row.unit_price),
      }))
      .filter((row) => row.description && !Number.isNaN(row.quantity) && !Number.isNaN(row.unit_price));

    if (payloadLines.length === 0) {
      showError('Add at least one line item with description and valid amounts.');
      return;
    }

    const payload = {
      client_id: recipientType === 'client' ? clientId : null,
      guest_name: recipientType === 'guest' ? guestName.trim() : null,
      guest_email: recipientType === 'guest' ? guestEmail.trim() || null : null,
      guest_phone: recipientType === 'guest' ? guestPhone.trim() || null : null,
      booking_id: bookingId || null,
      invoice_date: invoiceDate,
      due_date: dueDate,
      tax_rate: taxRate === '' ? null : parseFloat(taxRate),
      discount_type: discountType || null,
      discount_value:
        discountType && discountValue !== '' ? parseFloat(discountValue) : null,
      logo_url: logoUrl || null,
      notes: notes.trim() || null,
      payment_terms: paymentTerms.trim() || null,
      status,
      line_items: payloadLines,
    };

    if (isEdit) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleGenerateNotes = async () => {
    if (!canUseAiGenerator || isGeneratingNotes) return;

    setNotesAiError('');
    setIsGeneratingNotes(true);

    try {
      const recipientName =
        recipientType === 'client'
          ? clients.find((c) => String(c.id) === String(clientId))?.name
          : guestName.trim();
      const lineSummary = lineItems
        .filter((row) => row.description?.trim())
        .map((row) => row.description.trim())
        .slice(0, 5)
        .join('; ');

      const prompt = [
        `Write a short professional invoice note for ${vendorLabel}.`,
        recipientName ? `Client/recipient: ${recipientName}.` : '',
        lineSummary ? `Line items: ${lineSummary}.` : '',
        'Tone: warm, courteous, business-appropriate.',
        '1-3 short sentences. Suitable for an invoice footer notes field.',
        'Do not use hashtags, emojis, or bullet points.',
        'Output only the note text.',
      ]
        .filter(Boolean)
        .join(' ');

      const response = await supportChatApi.sendMessage({
        message: prompt,
        history: [],
      });

      const generated = normalizeChatReply(response?.data?.reply);
      if (!generated) {
        setNotesAiError('Could not generate notes. Please try again.');
        return;
      }

      setNotes(generated.slice(0, 1000));
    } catch (error) {
      setNotesAiError(
        error?.response?.data?.message || 'Could not generate notes. Please try again.'
      );
    } finally {
      setIsGeneratingNotes(false);
    }
  };

  const formatDisplayDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso + 'T12:00:00').toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return iso;
    }
  };

  if (isEdit && loadingInvoice) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-10 h-10 animate-spin text-gray-400" />
      </div>
    );
  }

  if (isEdit && invoiceError) {
    return (
      <div className="text-center py-16">
        <p className="text-gray-700 mb-4">Could not load invoice.</p>
        <Button type="button" variant="outline" onClick={() => navigate('/vendor/invoices')}>
          Back to invoices
        </Button>
      </div>
    );
  }

  if (clientsLoading && !isEdit) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-10 h-10 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <>
      <style>{`
        @media print {
          .invoice-no-print { display: none !important; }
          .invoice-print-area { display: block !important; width: 100% !important; max-width: none !important; }
        }
      `}</style>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 xl:gap-10 items-start">
        <form onSubmit={handleSubmit} className="space-y-6 invoice-no-print">
          <div className="vendor-card p-4 sm:p-5 space-y-4">
            <div>
              <p className="text-sm font-semibold text-gray-900 mb-2">Bill to</p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setRecipientType('client')}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                    recipientType === 'client'
                      ? 'bg-gray-900 text-white border-gray-900'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                  }`}
                >
                  Platform client
                </button>
                <button
                  type="button"
                  onClick={() => setRecipientType('guest')}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                    recipientType === 'guest'
                      ? 'bg-gray-900 text-white border-gray-900'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                  }`}
                >
                  Guest / external
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Use guest / external for private clients, walk-ins, or any invoice not tied to a platform account.
              </p>
            </div>

            {recipientType === 'client' ? (
              <CustomSelect
                label="Platform client *"
                value={clientId}
                onChange={(v) => setClientId(v)}
                options={clientOptions}
                placeholder={clients.length ? 'Select a client' : 'No platform clients yet — use guest / external'}
                size="sm"
              />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  label="Contact name *"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  placeholder="e.g. Jane Smith"
                />
                <Input
                  label="Email (optional)"
                  type="email"
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                  placeholder="client@example.com"
                />
                <div className="sm:col-span-2">
                  <Input
                    label="Phone (optional)"
                    value={guestPhone}
                    onChange={(e) => setGuestPhone(e.target.value)}
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>
            )}

            <CustomSelect
              label="Link booking (optional)"
              value={bookingId}
              onChange={handleBookingChange}
              options={bookingOptions}
              placeholder={bookingsLoading ? 'Loading bookings…' : 'Standalone invoice — no booking'}
              disabled={bookingsLoading}
              size="sm"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <DatePicker
              label="Invoice date *"
              value={invoiceDate}
              onChange={setInvoiceDate}
              placeholder="Select date"
              allowPastDates
            />
            <DatePicker
              label="Due date *"
              value={dueDate}
              onChange={setDueDate}
              placeholder="Select date"
              allowPastDates
              minDate={invoiceDate || undefined}
            />
          </div>

          <div className="vendor-card p-4 sm:p-5">
            <BrandImagePicker
              label="Invoice logo"
              value={logoUrl}
              onChange={setLogoUrl}
              hint="Appears on the invoice preview, PDF, and email. Defaults to your business logo."
              aspectClass="aspect-[2/1] max-h-32"
              uploadImageType="general"
            />
            {!logoUrl && profileLogo && (
              <button
                type="button"
                onClick={() => setLogoUrl(profileLogo)}
                className="mt-2 text-xs font-medium text-primary-600 hover:underline"
              >
                Use business profile logo
              </button>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-semibold text-gray-900">Line items *</label>
              <button
                type="button"
                onClick={addLine}
                className="inline-flex items-center gap-1 text-sm font-medium text-gray-800 hover:text-black"
              >
                <Plus className="w-4 h-4" />
                Add line
              </button>
            </div>
            <div className="border border-gray-200 rounded-lg overflow-hidden divide-y divide-gray-100">
              {lineItems.map((row, index) => (
                <div key={row.id} className="p-3 sm:p-4 bg-white space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-xs font-medium text-gray-500 uppercase">Line {index + 1}</span>
                    {lineItems.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeLine(row.id)}
                        className="p-1.5 text-gray-400 hover:text-red-600 rounded-md hover:bg-red-50"
                        aria-label="Remove line"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <Input
                    value={row.description}
                    onChange={(e) => updateLine(row.id, 'description', e.target.value)}
                    placeholder="Description (e.g. Photo booth — 4 hours)"
                    className="w-full"
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Qty</label>
                      <Input
                        type="number"
                        min="0.01"
                        step="any"
                        value={row.quantity}
                        onChange={(e) => updateLine(row.id, 'quantity', e.target.value)}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Unit price ($)</label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={row.unit_price}
                        onChange={(e) => updateLine(row.id, 'unit_price', e.target.value)}
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="vendor-card p-4 sm:p-5 space-y-4">
            <div>
              <p className="text-sm font-semibold text-gray-900 mb-1">Discount (optional)</p>
              <p className="text-xs text-gray-500 mb-3">
                Applied before tax. Choose a fixed dollar amount or a percent of the subtotal.
              </p>
              <div className="flex flex-wrap gap-2 mb-3">
                {[
                  { value: '', label: 'No discount' },
                  { value: 'fixed', label: 'Fixed $' },
                  { value: 'percent', label: 'Percent %' },
                ].map((opt) => (
                  <button
                    key={opt.value || 'none'}
                    type="button"
                    onClick={() => {
                      setDiscountType(opt.value);
                      if (!opt.value) setDiscountValue('');
                    }}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                      discountType === opt.value
                        ? 'bg-gray-900 text-white border-gray-900'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {discountType && (
                <Input
                  label={discountType === 'percent' ? 'Discount percent' : 'Discount amount ($)'}
                  type="number"
                  min="0"
                  max={discountType === 'percent' ? '100' : undefined}
                  step="0.01"
                  value={discountValue}
                  onChange={(e) => setDiscountValue(e.target.value)}
                  placeholder={discountType === 'percent' ? 'e.g. 10' : 'e.g. 50.00'}
                />
              )}
              {discountAmount > 0 && (
                <p className="text-xs text-emerald-700 mt-2">
                  Discount applied: −${discountAmount.toFixed(2)}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-1">Tax rate (%)</label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={taxRate}
                  onChange={(e) => setTaxRate(e.target.value)}
                  placeholder="0"
                />
              </div>
              <CustomSelect
                label="Status"
                value={status}
                onChange={setStatus}
                options={
                  isEdit
                    ? [
                        { value: 'draft', label: 'Draft' },
                        { value: 'sent', label: 'Sent' },
                        { value: 'overdue', label: 'Overdue' },
                      ]
                    : [
                        { value: 'draft', label: 'Draft' },
                        { value: 'sent', label: 'Sent' },
                      ]
                }
                size="sm"
                className="!min-h-[2.75rem]"
              />
            </div>

            <div className="rounded-lg bg-white border border-gray-200 px-3 py-2 text-sm space-y-1">
              <div className="flex justify-between text-gray-600">
                <span>Subtotal</span>
                <span className="tabular-nums">${subtotal.toFixed(2)}</span>
              </div>
              {discountAmount > 0 && (
                <div className="flex justify-between text-emerald-700">
                  <span>Discount</span>
                  <span className="tabular-nums">−${discountAmount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-gray-600">
                <span>Tax{taxRateNum > 0 ? ` (${taxRateNum}%)` : ''}</span>
                <span className="tabular-nums">${taxAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-semibold text-gray-900 pt-1 border-t border-gray-100">
                <span>Total</span>
                <span className="tabular-nums">${total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-1">Payment terms (optional)</label>
            <textarea
              value={paymentTerms}
              onChange={(e) => setPaymentTerms(e.target.value)}
              rows={2}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
              placeholder="e.g. Net 14, due on receipt"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-1">Notes (optional)</label>
            <div className="relative">
              <textarea
                value={notes}
                onChange={(e) => {
                  setNotes(e.target.value);
                  if (notesAiError) setNotesAiError('');
                }}
                rows={3}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 pr-12 pb-10 text-sm focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                placeholder="Thank you for your business"
              />
              {canUseAiGenerator ? (
                <button
                  type="button"
                  onClick={handleGenerateNotes}
                  disabled={isGeneratingNotes}
                  title="Generate notes with AI"
                  aria-label="Generate notes with AI"
                  className="absolute bottom-2.5 right-2.5 inline-flex h-8 w-8 items-center justify-center rounded-full bg-emerald-600 text-white shadow-sm hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isGeneratingNotes ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Bot className="h-4 w-4" />
                  )}
                </button>
              ) : (
                <span
                  className="absolute bottom-2.5 right-2.5 inline-flex h-8 w-8 items-center justify-center rounded-full bg-emerald-600/40 text-white cursor-not-allowed"
                  title="Upgrade to a paid plan to use AI notes"
                  aria-label="AI notes locked on free plan"
                  role="img"
                >
                  <Bot className="h-4 w-4" />
                </span>
              )}
            </div>
            {notesAiError && (
              <p className="mt-1 text-xs text-red-600">{notesAiError}</p>
            )}
            {!canUseAiGenerator && (
              <p className="mt-1.5 text-xs text-gray-500">
                AI notes are available on paid plans.{' '}
                <Link to="/vendor/plan" className="text-primary-600 hover:underline font-medium">
                  Upgrade
                </Link>
              </p>
            )}
          </div>

          <div className="flex flex-wrap gap-3 pt-2">
            <Button
              type="submit"
              loading={createMutation.isPending || updateMutation.isPending}
              disabled={createMutation.isPending || updateMutation.isPending}
              icon={createMutation.isPending || updateMutation.isPending ? Loader2 : undefined}
            >
              {isEdit ? 'Save changes' : 'Create invoice'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                navigate(isEdit ? `/vendor/invoices/${editInvoiceId}` : '/vendor/invoices')
              }
            >
              Cancel
            </Button>
            <Button type="button" variant="outline" onClick={handlePrint} icon={Printer}>
              Print preview
            </Button>
          </div>
        </form>

        <div className="xl:sticky xl:top-24 space-y-3 invoice-print-area">
          <div className="flex items-center justify-between invoice-no-print">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Live preview</h3>
            <span className="text-xs text-gray-400">Updates as you type</span>
          </div>
          <InvoiceLivePreview
            vendorLabel={vendorLabel}
            vendorLogoUrl={logoUrl || profileLogo}
            vendorAddressLines={vendorAddressLines}
            billToName={billToPreview.name}
            billToEmail={billToPreview.email}
            billToPhone={billToPreview.phone}
            invoiceNumberPreview={isEdit && previewInvoiceNumber ? previewInvoiceNumber : '(assigned on save)'}
            invoiceDate={formatDisplayDate(invoiceDate)}
            dueDate={formatDisplayDate(dueDate)}
            lineItems={lineItems.map((r) => ({
              description: r.description,
              quantity: parseFloat(r.quantity) || 0,
              unit_price: parseFloat(r.unit_price) || 0,
            }))}
            taxRate={taxRateNum}
            subtotal={subtotal}
            discountAmount={discountAmount}
            discountType={discountType}
            discountValue={discountValue ? parseFloat(discountValue) : 0}
            taxAmount={taxAmount}
            total={total}
            notes={notes}
            paymentTerms={paymentTerms}
            bookingRef={
              selectedBooking
                ? selectedBooking.booking_reference || selectedBooking.id?.slice(0, 8)
                : null
            }
            statusLabel={
              status === 'sent' ? 'Sent' : status === 'overdue' ? 'Overdue' : 'Draft'
            }
          />
        </div>
      </div>
    </>
  );
};

export default CustomInvoiceBuilder;
